// AllTradesHistory/index.jsx - Properly Designed with XLSX Export
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Stack,
  Chip,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Container,
  Alert,
  AlertTitle,
  TextField,
  alpha,
  useMediaQuery,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ShowChart as ShowChartIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  Layers as LayersIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import AllTradesTable from './AllTradesTable';
import StrategyBreakdown from './StrategyBreakdown';
import PnLChart from '../TradeHistory/PnLChart';
import { COLORS, formatPnL } from '../TradeHistory/constants';

// ==================== CONSTANTS ====================
const DEFAULT_SLIPPAGE_COST_PCT = 0.05;

// ==================== HELPER FUNCTIONS ====================
const formatPercent = (value, showSign = true) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  const num = Number(value);
  const sign = showSign ? (num >= 0 ? '+' : '') : '';
  return `${sign}${num.toFixed(2)}%`;
};

// ==================== PERFORMANCE SUMMARY (TRADINGVIEW STYLE) ====================
const StatRow = ({ label, value, valueColor = COLORS.text.primary, suffix = '', isLast = false }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      py: 0.5,
      borderBottom: isLast ? 'none' : `1px solid ${alpha(COLORS.border, 0.4)}`,
    }}
  >
    <Typography sx={{ color: COLORS.text.muted, fontSize: '0.68rem' }}>
      {label}
    </Typography>
    <Typography 
      sx={{ 
        color: valueColor, 
        fontSize: '0.72rem', 
        fontWeight: 600, 
        fontFamily: 'monospace',
      }}
    >
      {value}{suffix}
    </Typography>
  </Box>
);

const PerformanceSummary = ({ stats, trades }) => {
  const computedStats = useMemo(() => {
    if (!trades?.length) {
      return {
        avgAdjustedWinPct: 0,
        avgAdjustedLossPct: 0,
        largestAdjustedWinPct: 0,
        largestAdjustedLossPct: 0,
        adjustedProfitFactor: 0,
        maxWinStreak: 0,
        maxLossStreak: 0,
      };
    }

    const slippageCostPct = DEFAULT_SLIPPAGE_COST_PCT;
    const validTrades = trades.filter(t => t.pnl_pct !== null && t.pnl_pct !== undefined);
    const getAdjustedPct = (t) => (parseFloat(t.pnl_pct) || 0) - slippageCostPct;

    const adjustedWins = validTrades.filter(t => getAdjustedPct(t) > 0);
    const adjustedLosses = validTrades.filter(t => getAdjustedPct(t) <= 0);

    const totalAdjustedWinPct = adjustedWins.reduce((sum, t) => sum + getAdjustedPct(t), 0);
    const totalAdjustedLossPct = Math.abs(adjustedLosses.reduce((sum, t) => sum + getAdjustedPct(t), 0));

    let currentWinStreak = 0, currentLossStreak = 0, maxWinStreak = 0, maxLossStreak = 0;
    validTrades.forEach(t => {
      if (getAdjustedPct(t) > 0) {
        currentWinStreak++;
        currentLossStreak = 0;
        maxWinStreak = Math.max(maxWinStreak, currentWinStreak);
      } else {
        currentLossStreak++;
        currentWinStreak = 0;
        maxLossStreak = Math.max(maxLossStreak, currentLossStreak);
      }
    });

    return {
      avgAdjustedWinPct: adjustedWins.length > 0 ? totalAdjustedWinPct / adjustedWins.length : 0,
      avgAdjustedLossPct: adjustedLosses.length > 0 ? totalAdjustedLossPct / adjustedLosses.length : 0,
      largestAdjustedWinPct: adjustedWins.length > 0 ? Math.max(...adjustedWins.map(getAdjustedPct)) : 0,
      largestAdjustedLossPct: adjustedLosses.length > 0 ? Math.abs(Math.min(...adjustedLosses.map(getAdjustedPct))) : 0,
      adjustedProfitFactor: totalAdjustedLossPct > 0 ? totalAdjustedWinPct / totalAdjustedLossPct : (totalAdjustedWinPct > 0 ? Infinity : 0),
      maxWinStreak,
      maxLossStreak,
    };
  }, [trades]);

  const netReturnColor = stats.totalAdjustedPnLPct >= 0 ? COLORS.success : COLORS.danger;
  const winRateColor = parseFloat(stats.adjustedWinRate) >= 50 ? COLORS.success : COLORS.danger;
  const profitFactorColor = computedStats.adjustedProfitFactor >= 1 ? COLORS.success : COLORS.danger;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Net Return Header */}
      <Box 
        sx={{ 
          textAlign: 'center', 
          pb: 1, 
          mb: 0.75, 
          borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        <Typography 
          sx={{ 
            color: netReturnColor, 
            fontSize: '1.4rem', 
            fontWeight: 700, 
            fontFamily: 'monospace',
            lineHeight: 1,
          }}
        >
          {formatPercent(stats.totalAdjustedPnLPct)}
        </Typography>
        <Typography sx={{ color: COLORS.text.muted, fontSize: '0.55rem', mt: 0.25 }}>
          Net Return (after {DEFAULT_SLIPPAGE_COST_PCT}% costs)
        </Typography>
      </Box>

      {/* Stats List */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <StatRow 
          label="Total Trades" 
          value={stats.total} 
        />
        <StatRow 
          label="Win Rate" 
          value={stats.adjustedWinRate} 
          valueColor={winRateColor}
          suffix="%" 
        />
        <StatRow 
          label="Profit Factor" 
          value={computedStats.adjustedProfitFactor === Infinity ? '∞' : computedStats.adjustedProfitFactor.toFixed(2)} 
          valueColor={profitFactorColor}
        />
        <StatRow 
          label="Winners" 
          value={stats.adjustedWinning} 
          valueColor={COLORS.success}
        />
        <StatRow 
          label="Losers" 
          value={stats.adjustedLosing} 
          valueColor={COLORS.danger}
        />
        <StatRow 
          label="Avg Win" 
          value={`+${computedStats.avgAdjustedWinPct.toFixed(2)}`} 
          valueColor={COLORS.success}
          suffix="%" 
        />
        <StatRow 
          label="Avg Loss" 
          value={`-${Math.abs(computedStats.avgAdjustedLossPct).toFixed(2)}`} 
          valueColor={COLORS.danger}
          suffix="%" 
        />
        <StatRow 
          label="Best Trade" 
          value={`+${computedStats.largestAdjustedWinPct.toFixed(2)}`} 
          valueColor={COLORS.success}
          suffix="%" 
        />
        <StatRow 
          label="Worst Trade" 
          value={`-${Math.abs(computedStats.largestAdjustedLossPct).toFixed(2)}`} 
          valueColor={COLORS.danger}
          suffix="%" 
        />
        <StatRow 
          label="Max Win Streak" 
          value={computedStats.maxWinStreak} 
          valueColor={COLORS.success}
        />
        <StatRow 
          label="Max Loss Streak" 
          value={computedStats.maxLossStreak} 
          valueColor={COLORS.danger}
          isLast
        />
      </Box>

      {/* Footer with raw return */}
      <Box 
        sx={{ 
          pt: 0.75, 
          borderTop: `1px solid ${COLORS.border}`,
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ color: COLORS.text.muted, fontSize: '0.55rem' }}>
          Raw Return: <span style={{ color: stats.totalPnLPct >= 0 ? COLORS.success : COLORS.danger, fontWeight: 600 }}>{formatPercent(stats.totalPnLPct)}</span>
        </Typography>
      </Box>
    </Box>
  );
};

// ==================== MAIN COMPONENT ====================
const AllTradesHistory = ({ apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // FIX: Default to 'all' so open/current trades show up
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDirection, setFilterDirection] = useState('all');
  const [filterTimeframe, setFilterTimeframe] = useState('all');
  const [filterTradingHour, setFilterTradingHour] = useState('all');
  const [filterStrategy, setFilterStrategy] = useState('all');
  const [limit, setLimit] = useState(500);
  const [dateRange, setDateRange] = useState({
    start: '2025-01-01',
    end: new Date().toISOString().split('T')[0],
  });

  const loadAllTrades = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        start_date: `${dateRange.start}T00:00:00`,
        end_date: `${dateRange.end}T23:59:59`,
        status: filterStatus,
        direction: filterDirection,
        timeframe: filterTimeframe,
        limit: limit.toString(),
      });

      const url = `${apiBaseUrl}/strategies/analyze/all/trades/?${params}`;
      const response = await fetch(url);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Failed to load trades');

      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, dateRange, filterStatus, filterDirection, filterTimeframe, limit]);

  useEffect(() => {
    loadAllTrades();
  }, [loadAllTrades]);

  const filterOptions = useMemo(() => {
    if (!data?.trades) return { timeframes: [], tradingHours: [], strategies: [] };
    return {
      timeframes: [...new Set(data.trades.map(t => t.timeframe))].filter(Boolean).sort(),
      tradingHours: [...new Set(data.trades.map(t => t.trading_hour))].filter(Boolean).sort(),
      strategies: [...new Set(data.trades.map(t => t.bulk_strategy_id))].filter(Boolean).sort(),
    };
  }, [data?.trades]);

  const filteredTrades = useMemo(() => {
    if (!data?.trades) return [];
    return data.trades.filter(t => {
      if (filterStrategy !== 'all' && t.bulk_strategy_id !== filterStrategy) return false;
      if (filterTradingHour !== 'all' && t.trading_hour !== filterTradingHour) return false;
      return true;
    });
  }, [data?.trades, filterStrategy, filterTradingHour]);

  const stats = useMemo(() => {
    const trades = filteredTrades;
    const total = trades.length;
    const slippageCostPct = DEFAULT_SLIPPAGE_COST_PCT;

    // Only compute PnL stats from completed trades with valid pnl
    const completedWithPnl = trades.filter(t => t.status === 'completed' && t.pnl !== null && t.pnl !== undefined);
    const winning = completedWithPnl.filter(t => (parseFloat(t.pnl) || 0) > 0).length;
    const losing = completedWithPnl.filter(t => (parseFloat(t.pnl) || 0) <= 0).length;
    const totalPnL = completedWithPnl.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
    const totalPnLPct = completedWithPnl.reduce((sum, t) => sum + (parseFloat(t.pnl_pct) || 0), 0);

    const adjustedWinning = completedWithPnl.filter(t => ((parseFloat(t.pnl_pct) || 0) - slippageCostPct) > 0).length;
    const adjustedLosing = completedWithPnl.length - adjustedWinning;
    const totalAdjustedPnLPct = completedWithPnl.reduce((sum, t) => sum + ((parseFloat(t.pnl_pct) || 0) - slippageCostPct), 0);

    return {
      total,
      winning,
      losing,
      winRate: completedWithPnl.length > 0 ? ((winning / completedWithPnl.length) * 100).toFixed(1) : '0.0',
      totalPnL,
      totalPnLPct,
      avgPnL: completedWithPnl.length > 0 ? totalPnL / completedWithPnl.length : 0,
      avgPnLPct: completedWithPnl.length > 0 ? totalPnLPct / completedWithPnl.length : 0,
      adjustedWinning,
      adjustedLosing,
      adjustedWinRate: completedWithPnl.length > 0 ? ((adjustedWinning / completedWithPnl.length) * 100).toFixed(1) : '0.0',
      totalAdjustedPnLPct,
      avgAdjustedPnLPct: completedWithPnl.length > 0 ? totalAdjustedPnLPct / completedWithPnl.length : 0,
      slippageCostPct,
    };
  }, [filteredTrades]);

  const chartTrades = useMemo(() => {
    // Only chart completed trades with PnL for cumulative curve
    return filteredTrades
      .filter(t => t.status === 'completed' && t.pnl_pct !== null && t.pnl_pct !== undefined)
      .map((trade, index) => ({
        ...trade,
        trade_number: index + 1,
        adjusted_pnl_pct: (parseFloat(trade.pnl_pct) || 0) - DEFAULT_SLIPPAGE_COST_PCT,
      }));
  }, [filteredTrades]);

  const adjustedPnLColor = stats.totalAdjustedPnLPct >= 0 ? COLORS.success : COLORS.danger;

  return (
    <Box sx={{ bgcolor: COLORS.bg.primary, minHeight: '100vh', py: 3 }}>
      <Container maxWidth={false} sx={{ maxWidth: 1920 }}>
        {/* Header */}
        <Paper
          sx={{
            p: 2.5,
            mb: 2.5,
            bgcolor: COLORS.bg.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(COLORS.primary, 0.05)} 0%, ${alpha(COLORS.purple, 0.05)} 100%)`,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{ bgcolor: alpha(COLORS.primary, 0.1), '&:hover': { bgcolor: alpha(COLORS.primary, 0.2) } }}
              >
                <ArrowBackIcon sx={{ color: COLORS.primary }} />
              </IconButton>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LayersIcon sx={{ color: COLORS.purple }} />
                  <Typography variant="h5" sx={{ color: COLORS.text.primary, fontWeight: 700 }}>
                    All Strategies Trade History
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: COLORS.text.muted }}>
                  {data?.summary?.strategies_processed || 0} strategies • {stats.total} trades
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Tooltip title="Net Adjusted Return">
                <Chip
                  label={formatPercent(stats.totalAdjustedPnLPct)}
                  sx={{
                    bgcolor: alpha(adjustedPnLColor, 0.15),
                    color: adjustedPnLColor,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                    height: 32,
                  }}
                />
              </Tooltip>
              <Tooltip title="Adjusted Win Rate">
                <Chip
                  label={`${stats.adjustedWinRate}% WR`}
                  sx={{
                    bgcolor: alpha(parseFloat(stats.adjustedWinRate) >= 50 ? COLORS.success : COLORS.danger, 0.15),
                    color: parseFloat(stats.adjustedWinRate) >= 50 ? COLORS.success : COLORS.danger,
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    height: 32,
                  }}
                />
              </Tooltip>
              <Tooltip title="Total P&L">
                <Chip
                  label={formatPnL(stats.totalPnL)}
                  sx={{
                    bgcolor: alpha(stats.totalPnL >= 0 ? COLORS.success : COLORS.danger, 0.15),
                    color: stats.totalPnL >= 0 ? COLORS.success : COLORS.danger,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    height: 32,
                  }}
                />
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Refresh">
                <IconButton onClick={loadAllTrades} sx={{ bgcolor: alpha(COLORS.primary, 0.1), '&:hover': { bgcolor: alpha(COLORS.primary, 0.2) } }}>
                  <RefreshIcon sx={{ color: COLORS.primary }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2.5, bgcolor: COLORS.bg.card, border: `1px solid ${COLORS.border}`, borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: showFilters ? 2 : 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
              <Typography sx={{ color: COLORS.text.primary, fontWeight: 600 }}>Filters</Typography>
              <Chip label={`${filteredTrades.length} results`} size="small" sx={{ bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary, fontWeight: 600, fontSize: '0.7rem' }} />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button size="small" startIcon={<CalendarIcon />} onClick={() => setShowDatePicker(!showDatePicker)} sx={{ color: showDatePicker ? COLORS.primary : COLORS.text.muted }}>
                Date Range
              </Button>
              <IconButton size="small" onClick={() => setShowFilters(!showFilters)} sx={{ color: COLORS.text.muted }}>
                {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Stack>
          </Stack>

          <Collapse in={showDatePicker}>
            <Stack direction="row" spacing={2} sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}` }}>
              <TextField label="Start Date" type="date" value={dateRange.start} onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 160, '& .MuiOutlinedInput-root': { color: COLORS.text.primary, '& fieldset': { borderColor: alpha(COLORS.primary, 0.3) } }, '& .MuiInputLabel-root': { color: COLORS.text.muted } }} />
              <TextField label="End Date" type="date" value={dateRange.end} onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 160, '& .MuiOutlinedInput-root': { color: COLORS.text.primary, '& fieldset': { borderColor: alpha(COLORS.primary, 0.3) } }, '& .MuiInputLabel-root': { color: COLORS.text.muted } }} />
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select value={limit} onChange={(e) => setLimit(e.target.value)} sx={{ color: COLORS.text.primary, bgcolor: alpha(COLORS.bg.tertiary, 0.5), '.MuiOutlinedInput-notchedOutline': { borderColor: alpha(COLORS.primary, 0.3) } }}>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={250}>250</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                  <MenuItem value={2000}>2000</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Collapse>

          <Collapse in={showFilters}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} flexWrap="wrap">
              <Box>
                <Typography variant="caption" sx={{ color: COLORS.text.muted, mb: 0.5, display: 'block', fontWeight: 600 }}>Status</Typography>
                <ToggleButtonGroup value={filterStatus} exclusive onChange={(e, v) => v && setFilterStatus(v)} size="small">
                  <ToggleButton value="all" sx={{ color: COLORS.text.muted, '&.Mui-selected': { color: COLORS.primary, bgcolor: alpha(COLORS.primary, 0.1) } }}>All</ToggleButton>
                  <ToggleButton value="completed" sx={{ color: COLORS.text.muted, '&.Mui-selected': { color: COLORS.success, bgcolor: alpha(COLORS.success, 0.1) } }}>Completed</ToggleButton>
                  <ToggleButton value="open" sx={{ color: COLORS.text.muted, '&.Mui-selected': { color: COLORS.warning, bgcolor: alpha(COLORS.warning, 0.1) } }}>Open</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: COLORS.text.muted, mb: 0.5, display: 'block', fontWeight: 600 }}>Direction</Typography>
                <ToggleButtonGroup value={filterDirection} exclusive onChange={(e, v) => v && setFilterDirection(v)} size="small">
                  <ToggleButton value="all" sx={{ color: COLORS.text.muted, '&.Mui-selected': { color: COLORS.primary, bgcolor: alpha(COLORS.primary, 0.1) } }}>All</ToggleButton>
                  <ToggleButton value="LONG" sx={{ color: COLORS.text.muted, '&.Mui-selected': { color: COLORS.success, bgcolor: alpha(COLORS.success, 0.1) } }}><TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} /> Long</ToggleButton>
                  <ToggleButton value="SHORT" sx={{ color: COLORS.text.muted, '&.Mui-selected': { color: COLORS.danger, bgcolor: alpha(COLORS.danger, 0.1) } }}><TrendingDownIcon sx={{ fontSize: 16, mr: 0.5 }} /> Short</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: COLORS.text.muted, mb: 0.5, display: 'block', fontWeight: 600 }}>Timeframe</Typography>
                <ToggleButtonGroup value={filterTimeframe} exclusive onChange={(e, v) => v && setFilterTimeframe(v)} size="small">
                  <ToggleButton value="all" sx={{ color: COLORS.text.muted, '&.Mui-selected': { color: COLORS.primary, bgcolor: alpha(COLORS.primary, 0.1) } }}>All</ToggleButton>
                  {filterOptions.timeframes.map(tf => (
                    <ToggleButton key={tf} value={tf} sx={{ color: COLORS.text.muted, '&.Mui-selected': { color: COLORS.warning, bgcolor: alpha(COLORS.warning, 0.1) } }}>{tf}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: COLORS.text.muted, mb: 0.5, display: 'block', fontWeight: 600 }}>Trading Hour</Typography>
                <ToggleButtonGroup value={filterTradingHour} exclusive onChange={(e, v) => v && setFilterTradingHour(v)} size="small">
                  <ToggleButton value="all" sx={{ color: COLORS.text.muted, '&.Mui-selected': { color: COLORS.primary, bgcolor: alpha(COLORS.primary, 0.1) } }}>All</ToggleButton>
                  {filterOptions.tradingHours.map(th => (
                    <ToggleButton key={th} value={th} sx={{ color: COLORS.text.muted, '&.Mui-selected': { color: COLORS.purple, bgcolor: alpha(COLORS.purple, 0.1) } }}>{th}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: COLORS.text.muted, mb: 0.5, display: 'block', fontWeight: 600 }}>Strategy</Typography>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <Select value={filterStrategy} onChange={(e) => setFilterStrategy(e.target.value)} sx={{ color: COLORS.text.primary, bgcolor: alpha(COLORS.bg.tertiary, 0.5), '.MuiOutlinedInput-notchedOutline': { borderColor: alpha(COLORS.primary, 0.2) } }} MenuProps={{ PaperProps: { sx: { maxHeight: 400, bgcolor: COLORS.bg.secondary } } }}>
                    <MenuItem value="all">All Strategies ({filterOptions.strategies.length})</MenuItem>
                    {filterOptions.strategies.map(s => (
                      <MenuItem key={s} value={s} sx={{ fontSize: '0.85rem' }}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </Collapse>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress sx={{ color: COLORS.primary }} />
              <Typography sx={{ color: COLORS.text.muted }}>Loading trades from all strategies...</Typography>
            </Stack>
          </Box>
        ) : (
          <>
            {/* Chart (50%) + Summary (30%) + Empty (20%) */}
            {filteredTrades.length > 0 && (
              <Stack 
                direction={{ xs: 'column', lg: 'row' }} 
                spacing={2.5} 
                sx={{ mb: 2.5 }}
              >
                {/* Chart - 50% */}
                <Paper 
                  sx={{ 
                    p: 2.5, 
                    bgcolor: COLORS.bg.card, 
                    border: `1px solid ${COLORS.border}`, 
                    borderRadius: 2, 
                    height: 480,
                    flex: { lg: '0 0 calc(50% - 10px)' },
                    minWidth: 0,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <ShowChartIcon sx={{ color: COLORS.primary }} />
                      <Typography variant="h6" sx={{ color: COLORS.text.primary, fontWeight: 700 }}>Cumulative P&L</Typography>
                    </Stack>
                    <Chip label={formatPnL(stats.totalPnL)} sx={{ bgcolor: alpha(stats.totalPnL >= 0 ? COLORS.success : COLORS.danger, 0.15), color: stats.totalPnL >= 0 ? COLORS.success : COLORS.danger, fontWeight: 700, fontFamily: 'monospace', fontSize: '0.9rem' }} />
                  </Stack>
                  <PnLChart trades={chartTrades} height={390} />
                </Paper>

                {/* Performance Summary - 30% */}
                <Paper 
                  sx={{ 
                    p: 1.25, 
                    bgcolor: COLORS.bg.card, 
                    border: `1px solid ${COLORS.border}`, 
                    borderRadius: 2, 
                    height: 480,
                    flex: { lg: '0 0 calc(30% - 10px)' },
                    minWidth: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5, pb: 0.5, borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
                    <AssessmentIcon sx={{ color: COLORS.purple, fontSize: 14 }} />
                    <Typography sx={{ color: COLORS.text.primary, fontWeight: 700, fontSize: '0.75rem' }}>Performance</Typography>
                  </Stack>
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <PerformanceSummary stats={stats} trades={chartTrades} />
                  </Box>
                </Paper>

                {/* Empty space - 20% (just let flex handle it) */}
              </Stack>
            )}

            {/* Strategy Breakdown */}
            {data?.strategy_breakdown?.length > 0 && filterStrategy === 'all' && (
              <Paper sx={{ p: 2.5, mb: 2.5, bgcolor: COLORS.bg.card, border: `1px solid ${COLORS.border}`, borderRadius: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <LayersIcon sx={{ color: COLORS.warning }} />
                  <Typography variant="h6" sx={{ color: COLORS.text.primary, fontWeight: 700 }}>Strategy Performance</Typography>
                  <Chip label={`${data.strategy_breakdown.length} strategies`} size="small" sx={{ bgcolor: alpha(COLORS.warning, 0.1), color: COLORS.warning, fontWeight: 600 }} />
                </Stack>
                <StrategyBreakdown strategies={data.strategy_breakdown} trades={data.trades || []} />
              </Paper>
            )}

            {/* Trade Table */}
            <Paper sx={{ bgcolor: COLORS.bg.card, border: `1px solid ${COLORS.border}`, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS.border}` }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <HistoryIcon sx={{ color: COLORS.primary }} />
                  <Typography variant="h6" sx={{ color: COLORS.text.primary, fontWeight: 700 }}>Trade Log</Typography>
                  <Chip label={`${filteredTrades.length} trades`} size="small" sx={{ bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary, fontWeight: 600 }} />
                </Stack>
              </Box>
              <AllTradesTable trades={filteredTrades} />
            </Paper>
          </>
        )}
      </Container>
    </Box>
  );
};

export default AllTradesHistory;
